import { useQueryClient } from "@tanstack/react-query";
import { useAxios } from "auth-sdk";
import { CheckboxField, Flex, Form, FormGroup, FormGroupsLayout, Text } from "@wayflyer/flyui";
import type { BankAccount, BankConnectionSchema, FilterBankAccountsParams } from "bankconnect";
import { useReducer, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useErrorModal } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { COMMON_MESSAGES } from "../../../../components/common-messages";
import { BANK_CONNECT_API, BANK_CONNECT_SOURCES_API } from "../../../../components/constants";
import { useBankConnectContext } from "../context/use-bank-connect-context";

export const ConfirmAccounts = ({ bankConnections }: { bankConnections: BankConnectionSchema[] }) => {
  const intl = useIntl();
  const client = useAxios();
  const queryClient = useQueryClient();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const [loading, setLoading] = useState(false);
  const { openErrorModal } = useErrorModal();
  const { filterAccountsPath } = useBankConnectContext();
  const [accountsSelected, setAccountsSelected] = useState(false);

  const filterBankAccountsParamsReducer = (
    state: FilterBankAccountsParams,
    action: { connection: BankConnectionSchema; account: BankAccount; selected: boolean }
  ) => {
    const newState: FilterBankAccountsParams = {
      connections: {
        ...state.connections,
        [action.connection.id]: {
          accounts: {
            ...state.connections[action.connection.id].accounts,
            [action.account.id]: { enabled: action.selected },
          },
        },
      },
    };
    const areAccountsSelected = Object.values(newState.connections).some((connectionAccountParams) =>
      Object.values(connectionAccountParams.accounts).some((bankParams) => bankParams.enabled)
    );
    setAccountsSelected(areAccountsSelected);
    return newState;
  };

  const buildFilterStateFromConnections = (connections: BankConnectionSchema[]): FilterBankAccountsParams => {
    const connectionsMap: FilterBankAccountsParams = { connections: {} };
    let areAccountsSelected = false;
    for (const connection of connections) {
      connectionsMap.connections[connection.id] = { accounts: {} };
      const accounts = connection.accounts || [];
      for (const account of accounts) {
        const isAccountSelected = connection.accountsSelected ? account.pull_enabled : true;
        connectionsMap.connections[connection.id].accounts[account.id] = {
          enabled: isAccountSelected,
        };
        areAccountsSelected = areAccountsSelected || isAccountSelected;
      }
    }
    setAccountsSelected(areAccountsSelected);
    return connectionsMap;
  };

  const [selectedAccounts, dispatchSelectedAccounts] = useReducer(
    filterBankAccountsParamsReducer,
    { connections: {} },
    () => buildFilterStateFromConnections(bankConnections)
  );

  const submitAccounts = async () => {
    setLoading(true);
    try {
      await client.post(filterAccountsPath, selectedAccounts);
      queryClient.invalidateQueries({
        queryKey: [BANK_CONNECT_API.replace(":customerSlug", customerDomain), customerDomain],
      });
      queryClient.invalidateQueries({
        queryKey: [BANK_CONNECT_SOURCES_API.replace(":customerSlug", customerDomain), customerDomain],
      });
    } catch (error: any) {
      openErrorModal({ variant: "submit-error", error });
    } finally {
      setLoading(false);
    }
  };

  const accountsForm = useForm();
  return (
    <Flex direction="column" gap="6">
      <Text>
        {intl.formatMessage({
          id: "funding-application: bank accounts filter - body",
          defaultMessage: "Please select all accounts for your business. We only pull data for selected accounts.",
        })}
      </Text>
      <Form
        onSubmit={accountsForm.handleSubmit(submitAccounts)}
        primaryAction={{
          content: intl.formatMessage(COMMON_MESSAGES.continueLabel),
          disabled: loading || !accountsSelected,
          loading,
          analyticsId: "submit-create-company",
        }}
        primaryActionTestId="company-creation-submit"
      >
        <FormGroupsLayout>
          {bankConnections.map((connection: BankConnectionSchema) => (
            <FormGroup key={connection.id} legend={connection.institutionName}>
              {connection.accounts.map((account) => (
                <CheckboxField
                  key={`select-account-${account.id}-field`}
                  name={`select-account-${account.id}-field`}
                  options={{
                    name: `select-account-${account.id}`,
                    label: account.mask
                      ? `${account.name} - ${account.type} - ******${account.mask}`
                      : `${account.name} - ${account.type}`,
                    checked: selectedAccounts.connections[connection.id]
                      ? selectedAccounts.connections[connection.id].accounts[account.id].enabled
                      : false,
                    onChange: (checked) => {
                      dispatchSelectedAccounts({
                        connection: connection,
                        account: account,
                        selected: checked,
                      });
                    },
                  }}
                />
              ))}
            </FormGroup>
          ))}
        </FormGroupsLayout>
      </Form>
    </Flex>
  );
};
